import { Decimal } from 'decimal.js';

// Set Decimal precision
Decimal.set({ precision: 30, rounding: 4 });

/**
 * Convert monthly interest rate to annual interest rate
 * @param {number|string} monthlyRate - Monthly interest rate (in percentage)
 * @returns {number} Annual interest rate as a percentage
 */
export function convertMonthlyToAnnualRate(monthlyRate) {
  if (!monthlyRate || monthlyRate === 0) return 0;
  // // Formula: Annual = ((1 + Monthly/100)^12 - 1) * 100
  // return ((Math.pow(1 + monthlyRate / 100, 12) - 1) * 100);

  return monthlyRate * 12;
}

/**
 * Convert annual interest rate to monthly interest rate
 * @param {number|string} annualRate - Annual interest rate (in percentage)
 * @returns {number} Monthly interest rate as a percentage
 */
export function convertAnnualToMonthlyRate(annualRate) {
  if (!annualRate || annualRate === 0) return 0;
  // // Formula: Monthly = ((1 + Annual/100)^(1/12) - 1) * 100
  // return ((Math.pow(1 + annualRate / 100, 1/12) - 1) * 100);

  return annualRate / 12;
}

/**
 * Calculate monthly payment for a loan
 * @param {number|string} principal - Loan amount
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} termInMonths - Loan term in months
 * @returns {Decimal} Monthly payment amount
 */
export function calculateMonthlyPayment(principal, annualInterestRate, termInMonths) {
  // Ensure inputs are Decimal instances
  const P = new Decimal(principal);
  const r = new Decimal(annualInterestRate).div(100).div(12); // Monthly interest rate
  const n = new Decimal(termInMonths);

  // Check if interest rate is zero
  if (r.isZero()) {
    return P.div(n);
  }

  // Standard loan payment formula: P * (r * (1 + r)^n) / ((1 + r)^n - 1)
  const numerator = r.times(r.add(1).pow(n));
  const denominator = r.add(1).pow(n).minus(1);
  
  return P.times(numerator.div(denominator));
}

/**
 * Add remaining total to repay column to each schedule row
 * @param {Array} schedule - Amortization schedule
 * @returns {Array} Schedule with remainingToRepay on each row
 */
export function addRemainingToRepayToSchedule(schedule) {
  if (!schedule || schedule.length === 0) return schedule;

  const totalRepaid = schedule.reduce(
    (sum, row) => sum.plus(new Decimal(row.payment)),
    new Decimal(0)
  );

  let remaining = totalRepaid;

  return schedule.map(row => {
    remaining = remaining.minus(new Decimal(row.payment));
    return {
      ...row,
      remainingToRepay: remaining.isNegative() ? new Decimal(0) : remaining
    };
  });
}

/**
 * Generate amortization schedule
 * @param {number|string} principal - Loan amount
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} termInMonths - Loan term in months
 * @returns {Array} Amortization schedule
 */
export function generateAmortizationSchedule(principal, annualInterestRate, termInMonths) {
  const P = new Decimal(principal);
  const monthlyInterestRate = new Decimal(annualInterestRate).div(100).div(12);
  const n = parseInt(termInMonths, 10);
  
  const monthlyPayment = calculateMonthlyPayment(P, annualInterestRate, n);
  
  const schedule = [];
  let remainingBalance = P;
  let totalInterestPaid = new Decimal(0);
  
  for (let month = 1; month <= n; month++) {
    // Calculate interest for this period
    const interestPayment = remainingBalance.times(monthlyInterestRate);
    
    // Calculate principal for this period
    let principalPayment;
    
    // For the last payment, pay off the entire remaining balance to avoid rounding issues
    if (month === n) {
      principalPayment = remainingBalance;
    } else {
      principalPayment = monthlyPayment.minus(interestPayment);
    }
    
    // Calculate new remaining balance
    remainingBalance = remainingBalance.minus(principalPayment);
    
    // Accumulate total interest
    totalInterestPaid = totalInterestPaid.plus(interestPayment);
    
    // Add to schedule
    schedule.push({
      month,
      payment: monthlyPayment,
      principal: principalPayment,
      interest: interestPayment,
      totalInterest: totalInterestPaid,
      balance: remainingBalance.isNegative() ? new Decimal(0) : remainingBalance
    });
  }
  
  return addRemainingToRepayToSchedule(schedule);
}

/**
 * Calculate total interest paid over the loan term
 * @param {number|string} principal - Loan amount
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} termInMonths - Loan term in months
 * @returns {Decimal} Total interest paid
 */
export function calculateTotalInterest(principal, annualInterestRate, termInMonths) {
  const monthlyPayment = calculateMonthlyPayment(principal, annualInterestRate, termInMonths);
  const totalPaid = monthlyPayment.times(termInMonths);
  return totalPaid.minus(principal);
}

/**
 * Calculate total amount repaid over the loan term
 * @param {number|string} principal - Loan amount
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} termInMonths - Loan term in months
 * @returns {Decimal} Total amount repaid
 */
export function calculateTotalRepaid(principal, annualInterestRate, termInMonths) {
  const monthlyPayment = calculateMonthlyPayment(principal, annualInterestRate, termInMonths);
  return monthlyPayment.times(termInMonths);
}

/**
 * Calculate Annual Percentage Rate (APR) for a standard loan
 * @param {number|string} principal - Loan amount
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} termInMonths - Loan term in months
 * @returns {Decimal} APR as a percentage
 */
export function calculateAPR(principal, annualInterestRate, termInMonths) {
  // Calculate the true compound APR based on the monthly rate used in calculations
  // Convert annual rate to monthly rate first
  const monthlyRate = new Decimal(annualInterestRate).div(100).div(12);
  
  // Then compound the monthly rate back to get true annual percentage rate
  // APR = ((1 + monthly_rate)^12 - 1) * 100
  const apr = monthlyRate.add(1).pow(12).minus(1).times(100);
  
  return apr;
}

/**
 * Calculate daily interest rate from annual interest rate
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @returns {Decimal} Daily interest rate as a percentage
 */
export function calculateDailyInterestRate(annualInterestRate) {
  const annualRate = new Decimal(annualInterestRate);
  return annualRate.div(365);
}

/**
 * Calculate daily interest amount
 * @param {number|string} principal - Current principal amount
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @returns {Decimal} Daily interest amount
 */
export function calculateDailyInterestAmount(principal, annualInterestRate) {
  const P = new Decimal(principal);
  const dailyRate = new Decimal(annualInterestRate).div(100).div(365);
  return P.times(dailyRate);
}

/**
 * Calculate effective APR based on principal and total repayment
 * @param {number|string} principal - Loan amount
 * @param {number|string} totalRepaid - Total amount repaid over the loan term
 * @param {number|string} termInMonths - Loan term in months
 * @returns {Decimal} Effective APR as a percentage
 */
export function calculateEffectiveAPR(principal, totalRepaid, termInMonths) {
  const P = new Decimal(principal);
  const TR = new Decimal(totalRepaid);
  const n = new Decimal(termInMonths);
  
  // If principal is zero, return zero
  if (P.isZero()) {
    return new Decimal(0);
  }
  
  // Calculate total interest
  const totalInterest = TR.minus(P);
  
  // If no interest is charged, return zero
  if (totalInterest.isZero() || totalInterest.isNegative()) {
    return new Decimal(0);
  }
  
  // Calculate the effective monthly rate that would produce this total repayment
  // Using the formula: Total = Principal * (1 + r)^n
  // Solve for r: r = (Total/Principal)^(1/n) - 1
  const ratio = TR.div(P);
  const exponent = new Decimal(1).div(n);
  
  try {
    // Calculate (Total/Principal)^(1/n) - 1 to get monthly rate
    const monthlyRate = ratio.pow(exponent).minus(1);
    
    // Convert to annual rate: ((1 + monthly_rate)^12 - 1) * 100
    const effectiveAnnualRate = monthlyRate.add(1).pow(12).minus(1).times(100);
    
    return effectiveAnnualRate;
  } catch (error) {
    // Fallback to simple interest calculation if compound calculation fails
    console.warn('Compound APR calculation failed, using simple interest approximation');
    const termInYears = n.div(12);
    const yearlyInterestRate = totalInterest.div(P.times(termInYears)).times(100);
    return yearlyInterestRate;
  }
}

/**
 * Calculate early repayment scenarios
 * @param {number|string} currentBalance - Current outstanding balance
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} remainingMonths - Remaining months in loan term
 * @param {number|string} currentMonthlyPayment - Current monthly payment
 * @param {number|string} earlyPaymentAmount - Amount of early payment
 * @param {string} scenarioType - Type of scenario to calculate ('both', 'reducePayment', 'reduceTerm')
 * @returns {Object} Early repayment calculation results
 */
export function calculateEarlyRepaymentScenarios(
  currentBalance,
  annualInterestRate,
  remainingMonths,
  currentMonthlyPayment,
  earlyPaymentAmount = 0,
  scenarioType = 'both'
) {
  const balance = new Decimal(currentBalance);
  const rate = new Decimal(annualInterestRate);
  const remainingTerm = parseInt(remainingMonths, 10);
  const monthlyPayment = new Decimal(currentMonthlyPayment);
  const earlyPayment = new Decimal(earlyPaymentAmount);

  // Calculate current loan totals (without early payment)
  const currentTotalInterest = calculateTotalInterest(balance, rate, remainingTerm);
  const currentTotalRepaid = calculateTotalRepaid(balance, rate, remainingTerm);

  // Full settlement calculation
  const fullSettlementAmount = balance;
  const interestSavingsFromFullSettlement = currentTotalInterest;

  const results = {
    // Full settlement
    fullSettlementAmount,
    interestSavingsFromFullSettlement,
    currentTotalInterest,
    currentTotalRepaid,
    
    // Current loan details
    originalBalance: balance,
    originalRemainingMonths: remainingTerm,
    originalMonthlyPayment: monthlyPayment
  };

  // Only calculate partial payment scenarios if early payment is provided and positive
  if (earlyPayment.isPositive() && earlyPayment.lessThan(balance)) {
    const newBalance = balance.minus(earlyPayment);

    // Scenario 1: Reduce monthly payment (keep same term)
    if (scenarioType === 'both' || scenarioType === 'reducePayment') {
      const newMonthlyPayment = calculateMonthlyPayment(newBalance, rate, remainingTerm);
      const newTotalInterest = calculateTotalInterest(newBalance, rate, remainingTerm);
      const newTotalRepaid = calculateTotalRepaid(newBalance, rate, remainingTerm);
      const monthlySavings = monthlyPayment.minus(newMonthlyPayment);
      const totalInterestSavings = currentTotalInterest.minus(newTotalInterest);
      const effectiveAPR = calculateEffectiveAPR(balance, newTotalRepaid.plus(earlyPayment), remainingTerm);

      results.reducePaymentScenario = {
        newBalance,
        newMonthlyPayment,
        monthlySavings,
        remainingMonths: remainingTerm,
        newTotalInterest,
        newTotalRepaid,
        totalSavings: totalInterestSavings,
        effectiveAPR,
        schedule: generateAmortizationSchedule(newBalance, rate, remainingTerm)
      };
    }

    // Scenario 2: Reduce term (keep same monthly payment)
    if (scenarioType === 'both' || scenarioType === 'reduceTerm') {
      try {
        // Calculate new term with current monthly payment
        const monthlyRate = rate.div(100).div(12);
        
        // Check if monthly payment is sufficient
        const monthlyInterest = newBalance.times(monthlyRate);
        if (monthlyPayment.greaterThan(monthlyInterest)) {
          // Use logarithm formula to calculate new term
          const factor1 = new Decimal(1).minus(newBalance.times(monthlyRate).div(monthlyPayment));
          if (factor1.greaterThan(0)) {
            const numerator = factor1.log().neg();
            const denominator = new Decimal(1).plus(monthlyRate).log();
            const newTerm = numerator.div(denominator).ceil().toNumber();
            
            if (newTerm > 0 && newTerm < remainingTerm) {
              const monthsSaved = remainingTerm - newTerm;
              const newTotalRepaid = monthlyPayment.times(newTerm);
              const newTotalInterest = newTotalRepaid.minus(newBalance);
              const totalInterestSavings = currentTotalInterest.minus(newTotalInterest);
              const effectiveAPR = calculateEffectiveAPR(balance, newTotalRepaid.plus(earlyPayment), newTerm);

              results.reduceTermScenario = {
                newBalance,
                monthlyPayment,
                newTermMonths: newTerm,
                monthsSaved,
                newTotalInterest,
                newTotalRepaid,
                totalSavings: totalInterestSavings,
                effectiveAPR,
                schedule: generateAmortizationSchedule(newBalance, rate, newTerm)
              };
            }
          }
        }
      } catch (error) {
        console.error('Error calculating reduce term scenario:', error);
        results.reduceTermScenario = {
          error: 'Unable to calculate reduced term scenario with current parameters'
        };
      }
    }
  }

  return results;
}

/**
 * Simulate missed payments by extending loan term
 * @param {number|string} principal - Loan amount
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} termInMonths - Loan term in months
 * @param {Array} missedPayments - Array of month numbers where payments were missed
 * @param {number|string} lateFee - Late payment fee amount
 * @returns {Object} Updated loan information with extended term
 */
export function simulateMissedPayments(
  principal, 
  annualInterestRate, 
  termInMonths, 
  missedPayments = [], 
  lateFee = 0
) {
  const P = new Decimal(principal);
  const rate = new Decimal(annualInterestRate);
  const n = parseInt(termInMonths, 10);
  const lateFeeDec = new Decimal(lateFee);
  
  // Calculate the original monthly payment
  const monthlyPayment = calculateMonthlyPayment(P, rate, n);
  
  // Number of missed payments
  const missedPaymentCount = missedPayments.length;
  
  // Calculate total late fees
  const totalLateFees = lateFeeDec.times(missedPaymentCount);
  
  // Create the schedule with proper balance calculations for missed payments
  const extendedSchedule = [];
  const missedPaymentMonths = missedPayments.map(month => parseInt(month, 10));
  const monthlyInterestRate = rate.div(100).div(12);
  
  let currentBalance = P;
  let cumulativeInterest = new Decimal(0);
  
  // Process the original term, handling missed payments correctly
  for (let month = 1; month <= n; month++) {
    const isMissed = missedPaymentMonths.includes(month);
    
    // Calculate interest for this period
    const interestPayment = currentBalance.times(monthlyInterestRate);
    
    let principalPayment;
    let paymentAmount;
    let feeAmount = new Decimal(0);
    
    if (isMissed) {
      // No payment made - but interest still accrues and late fees are added to balance
      principalPayment = new Decimal(0);
      paymentAmount = new Decimal(0);
      
      // Add late fee to the balance (it becomes part of the outstanding debt)
      if (lateFeeDec.isPositive()) {
        feeAmount = lateFeeDec;
        currentBalance = currentBalance.plus(lateFeeDec);
      }
      
      // Interest accrues and is capitalized (added to the balance)
      currentBalance = currentBalance.plus(interestPayment);
      
      // Record the accrued interest for tracking purposes
      cumulativeInterest = cumulativeInterest.plus(interestPayment);
      
      // Check if the loan has become unviable - monthly payment is less than monthly interest
      const nextMonthInterest = currentBalance.times(monthlyInterestRate);
      if (monthlyPayment.lessThan(nextMonthInterest)) {
        throw new Error(
          `Loan becomes unviable after missing payment in month ${month}. ` +
          `Monthly payment (£${monthlyPayment.toFixed(2)}) is less than monthly interest ` +
          `(£${nextMonthInterest.toFixed(2)}) on the outstanding balance of £${currentBalance.toFixed(2)}. ` +
          `The loan will never be paid off with the current payment structure.`
        );
      }
    } else {
      // Regular payment made
      principalPayment = monthlyPayment.minus(interestPayment);
      paymentAmount = monthlyPayment;
      
      // Update balance
      currentBalance = currentBalance.minus(principalPayment);
      
      // Only count paid interest in cumulative total
      cumulativeInterest = cumulativeInterest.plus(interestPayment);
    }
    
    // Add to schedule
    extendedSchedule.push({
      month,
      payment: paymentAmount,
      principal: principalPayment,
      interest: interestPayment, // Show interest that accrued regardless of whether payment was made
      totalInterest: cumulativeInterest,
      balance: currentBalance.isNegative() ? new Decimal(0) : currentBalance,
      missed: isMissed,
      lateFee: feeAmount
    });
  }
  
  // Now we need to add additional payments to cover the remaining balance
  // Note: Interest and late fees have already been capitalized into currentBalance
  // and we still need to account for the missed payment values that weren't made
  const missedPaymentValue = monthlyPayment.times(missedPaymentCount);
  const totalAmountToRepay = currentBalance.plus(missedPaymentValue);
  
  // Add additional months to pay off the remaining amount
  let additionalBalance = totalAmountToRepay;
  let actualAdditionalMonths = 0;
  let month = n;
  
  while (additionalBalance.isPositive()) {
    month++;
    actualAdditionalMonths++;
    
    // Calculate interest for this additional period
    const interestPayment = additionalBalance.times(monthlyInterestRate);
    
    // Check if monthly payment is sufficient to cover interest
    if (monthlyPayment.lessThanOrEqualTo(interestPayment)) {
      throw new Error(
        `Loan becomes unviable during repayment phase. ` +
        `Monthly payment (£${monthlyPayment.toFixed(2)}) is insufficient to cover monthly interest ` +
        `(£${interestPayment.toFixed(2)}) on remaining balance of £${additionalBalance.toFixed(2)}. ` +
        `The loan cannot be repaid with the current payment structure.`
      );
    }
    
    // Calculate principal payment
    let principalPayment = monthlyPayment.minus(interestPayment);
    
    // For the final payment, pay off remaining balance
    if (principalPayment.greaterThanOrEqualTo(additionalBalance)) {
      principalPayment = additionalBalance;
    }
    
    // Update balance and cumulative interest
    additionalBalance = additionalBalance.minus(principalPayment);
    cumulativeInterest = cumulativeInterest.plus(interestPayment);
    
    // Add to schedule
    extendedSchedule.push({
      month,
      payment: principalPayment.plus(interestPayment),
      principal: principalPayment,
      interest: interestPayment,
      totalInterest: cumulativeInterest,
      balance: additionalBalance.isNegative() ? new Decimal(0) : additionalBalance,
      missed: false,
      lateFee: new Decimal(0)
    });
    
    // Break if loan is paid off
    if (additionalBalance.isZero() || additionalBalance.isNegative()) {
      break;
    }
    
    // Safety check to prevent infinite loop
    if (actualAdditionalMonths > 120) {
      break;
    }
  }
  
  // Ensure we have at least as many additional months as missed payments
  if (actualAdditionalMonths < missedPaymentCount) {
    actualAdditionalMonths = missedPaymentCount;
  }
  
  // Calculate the extended term interest only (without late fees)
  let extendedTermInterestOnly = new Decimal(0);
  if (currentBalance.isPositive() && actualAdditionalMonths > 0) {
    // Calculate interest for extending the original remaining balance
    const tempSchedule = generateAmortizationSchedule(currentBalance, rate, actualAdditionalMonths);
    extendedTermInterestOnly = tempSchedule.reduce((sum, payment) => {
      return sum.plus(payment.interest);
    }, new Decimal(0));
  }
  
  // Calculate totals
  const originalTotalInterest = calculateTotalInterest(P, rate, n);
  const additionalInterest = cumulativeInterest.minus(originalTotalInterest);
  
  // Calculate the additional interest specifically due to late fees
  const lateFeeInterestCost = additionalInterest.minus(extendedTermInterestOnly);
  
  // Actual new term
  const actualNewTermMonths = n + actualAdditionalMonths;
  
  return {
    schedule: addRemainingToRepayToSchedule(extendedSchedule),
    missedPaymentsTotal: monthlyPayment.times(missedPaymentCount), // Total value of missed payments
    totalLateFees,
    additionalMonths: actualAdditionalMonths, // Actual additional months needed
    originalTermMonths: n,
    newTermMonths: actualNewTermMonths, // Actual new term
    additionalInterest,
    extendedTermInterestOnly, // Interest from just extending the term
    lateFeeInterestCost, // Additional interest specifically due to late fees
    totalInterestWithMissedPayments: cumulativeInterest,
    originalTotalInterest
  };
}

/**
 * Format currency amount
 * @param {number|string|Decimal} amount - Amount to format
 * @param {string} locale - Locale string (default: 'en-GB')
 * @param {string} currency - Currency code (default: 'GBP')
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, locale = 'en-GB', currency = 'GBP') {
  // Handle Decimal objects
  const numericAmount = typeof amount === 'object' && amount.toNumber ? amount.toNumber() : parseFloat(amount);
  
  if (isNaN(numericAmount)) {
    return `${currency === 'GBP' ? '£' : '$'}0.00`;
  }
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericAmount);
  } catch (error) {
    // Fallback for unsupported locales
    const symbol = currency === 'GBP' ? '£' : '$';
    return `${symbol}${numericAmount.toFixed(2)}`;
  }
}

/**
 * Format percentage with specified decimal places
 * @param {number|string|Decimal} amount - Percentage amount to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string
 */
export function formatPercentage(amount, decimals = 2) {
  // Handle Decimal objects
  const numericAmount = typeof amount === 'object' && amount.toNumber ? amount.toNumber() : parseFloat(amount);
  
  if (isNaN(numericAmount)) {
    return '0.00%';
  }
  
  return `${numericAmount.toFixed(decimals)}%`;
}

// Date-based calculation utilities for more precise loan calculations

/**
 * Calculate the number of months between two dates
 * @param {Date|string} startDate - Start date
 * @param {Date|string} currentDate - Current date
 * @returns {number} Number of months between dates (can be fractional)
 */
export function calculateMonthsBetweenDates(startDate, currentDate) {
  const start = new Date(startDate);
  const current = new Date(currentDate);
  
  if (isNaN(start.getTime()) || isNaN(current.getTime())) {
    throw new Error('Invalid date format provided');
  }
  
  const yearDiff = current.getFullYear() - start.getFullYear();
  const monthDiff = current.getMonth() - start.getMonth();
  const dayDiff = current.getDate() - start.getDate();
  
  // Calculate total months with fractional component based on days
  let totalMonths = yearDiff * 12 + monthDiff;
  
  // Add fractional month based on day difference
  const daysInStartMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  totalMonths += dayDiff / daysInStartMonth;
  
  return Math.max(0, totalMonths); // Ensure non-negative
}

/**
 * Calculate precise remaining months from current date to loan end
 * @param {Date|string} startDate - Original loan start date
 * @param {Date|string} currentDate - Current date
 * @param {number} originalTermMonths - Original loan term in months
 * @returns {Object} Object with precise payment information
 */
export function calculatePreciseRemainingTerm(startDate, currentDate, originalTermMonths) {
  const monthsElapsed = calculateMonthsBetweenDates(startDate, currentDate);
  const remainingMonths = Math.max(0, originalTermMonths - monthsElapsed);
  const paymentsCompleted = Math.floor(monthsElapsed);
  const fractionalMonth = monthsElapsed - paymentsCompleted;
  
  return {
    monthsElapsed,
    remainingMonths,
    paymentsCompleted,
    fractionalMonth,
    isOverdue: monthsElapsed > originalTermMonths,
    daysSinceLastPayment: fractionalMonth * 30, // Approximate days
    nextPaymentDue: fractionalMonth < 1 ? (1 - fractionalMonth) * 30 : 0
  };
}

/**
 * Enhanced spread arrears function with optional date-based calculations
 * @param {number|string} currentBalance - Current outstanding balance
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} remainingMonths - Remaining months in original term
 * @param {number|string} arrearsAmount - Amount in arrears to be spread
 * @param {number|string} originalPrincipal - Original loan amount (optional)
 * @param {number|string} originalTermMonths - Original loan term in months (optional)
 * @param {Date|string} startDate - Original loan start date (optional)
 * @param {Date|string} currentDate - Current date (optional)
 * @returns {Object} Restructured payment information with enhanced date-based calculations
 */
export function spreadArrearsOverTermWithDates(currentBalance, annualInterestRate, remainingMonths, arrearsAmount, originalPrincipal = null, originalTermMonths = null, startDate = null, currentDate = null) {
  const balance = new Decimal(currentBalance);
  const arrears = new Decimal(arrearsAmount);
  
  // Use original terms if provided, otherwise fall back to current terms
  const origPrincipal = originalPrincipal ? new Decimal(originalPrincipal) : balance;
  const origTerm = originalTermMonths ? parseInt(originalTermMonths, 10) : parseInt(remainingMonths, 10);
  
  // Enhanced calculations with date precision if dates are provided
  let preciseTermInfo = null;
  let adjustedRemainingMonths = parseInt(remainingMonths, 10);
  
  if (startDate && currentDate) {
    try {
      preciseTermInfo = calculatePreciseRemainingTerm(startDate, currentDate, origTerm);
      adjustedRemainingMonths = Math.ceil(preciseTermInfo.remainingMonths);
      
      // If we're significantly into a payment period, we might want to adjust calculations
      if (preciseTermInfo.fractionalMonth > 0.5) {
        // More than halfway through the month - might want to consider this in arrears calculation
        adjustedRemainingMonths = Math.floor(preciseTermInfo.remainingMonths);
      }
    } catch (error) {
      console.warn('Date calculation failed, falling back to month-based calculation:', error.message);
    }
  }
  
  // Use adjusted remaining months if calculated from dates
  const finalRemainingMonths = preciseTermInfo ? adjustedRemainingMonths : parseInt(remainingMonths, 10);
  
  // New balance includes arrears
  const newBalance = balance.plus(arrears);
  
  // Calculate new monthly payment
  const newMonthlyPayment = calculateMonthlyPayment(newBalance, annualInterestRate, finalRemainingMonths);
  
  // Original monthly payment should be based on original loan terms
  const originalMonthlyPayment = calculateMonthlyPayment(origPrincipal, annualInterestRate, origTerm);
  
  // For spreading arrears, we want to compare:
  // 1. Current payment (without arrears) - what they would pay if no arrears
  // 2. New payment (with arrears spread) - what they'll pay with arrears included
  const currentPaymentWithoutArrears = calculateMonthlyPayment(balance, annualInterestRate, finalRemainingMonths);
  
  // Generate new schedule
  const newSchedule = generateAmortizationSchedule(newBalance, annualInterestRate, finalRemainingMonths);
  
  const result = {
    originalBalance: balance,
    arrearsAmount: arrears,
    newBalance,
    originalPrincipal: origPrincipal,
    originalTermMonths: origTerm,
    originalMonthlyPayment, // This is for reference/display
    currentPaymentWithoutArrears, // What they'd pay without arrears
    newMonthlyPayment, // What they'll pay with arrears spread
    increasedMonthlyAmount: newMonthlyPayment.minus(currentPaymentWithoutArrears),
    percentageIncrease: newMonthlyPayment.minus(currentPaymentWithoutArrears).div(currentPaymentWithoutArrears).times(100),
    remainingMonths: finalRemainingMonths,
    schedule: newSchedule
  };
  
  // Add date-based precision information if available
  if (preciseTermInfo) {
    result.dateBasedCalculation = {
      exactMonthsElapsed: preciseTermInfo.monthsElapsed,
      exactRemainingMonths: preciseTermInfo.remainingMonths,
      paymentsCompleted: preciseTermInfo.paymentsCompleted,
      daysSinceLastPayment: preciseTermInfo.daysSinceLastPayment,
      nextPaymentDue: preciseTermInfo.nextPaymentDue,
      isOverdue: preciseTermInfo.isOverdue,
      adjustedForTiming: adjustedRemainingMonths !== parseInt(remainingMonths, 10)
    };
  }
  
  return result;
}

/**
 * Restructure loan payment - spread arrears over remaining term
 * @param {number|string} currentBalance - Current outstanding balance
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} remainingMonths - Remaining months in original term
 * @param {number|string} arrearsAmount - Amount in arrears to be spread
 * @param {number|string} originalPrincipal - Original loan amount (optional, defaults to currentBalance)
 * @param {number|string} originalTermMonths - Original loan term in months (optional, defaults to remainingMonths)
 * @returns {Object} Restructured payment information
 */
export function spreadArrearsOverTerm(currentBalance, annualInterestRate, remainingMonths, arrearsAmount, originalPrincipal = null, originalTermMonths = null) {
  const balance = new Decimal(currentBalance);
  const arrears = new Decimal(arrearsAmount);
  
  // Use original terms if provided, otherwise fall back to current terms
  const origPrincipal = originalPrincipal ? new Decimal(originalPrincipal) : balance;
  const origTerm = originalTermMonths ? parseInt(originalTermMonths, 10) : parseInt(remainingMonths, 10);
  
  // New balance includes arrears
  const newBalance = balance.plus(arrears);
  
  // Calculate new monthly payment (with arrears spread)
  const newMonthlyPayment = calculateMonthlyPayment(newBalance, annualInterestRate, remainingMonths);
  
  // For spreading arrears, we want to compare:
  // 1. Current payment (without arrears) - what they would pay if no arrears
  // 2. New payment (with arrears spread) - what they'll pay with arrears included
  const currentPaymentWithoutArrears = calculateMonthlyPayment(balance, annualInterestRate, remainingMonths);
  
  // Original monthly payment based on original loan terms (for reference/display)
  const originalMonthlyPayment = calculateMonthlyPayment(origPrincipal, annualInterestRate, origTerm);
  
  // Generate new schedule
  const newSchedule = generateAmortizationSchedule(newBalance, annualInterestRate, remainingMonths);
  
  return {
    originalBalance: balance,
    arrearsAmount: arrears,
    newBalance,
    originalPrincipal: origPrincipal,
    originalTermMonths: origTerm,
    originalMonthlyPayment, // This is for reference/display
    currentPaymentWithoutArrears, // What they'd pay without arrears
    newMonthlyPayment, // What they'll pay with arrears spread
    increasedMonthlyAmount: newMonthlyPayment.minus(currentPaymentWithoutArrears),
    percentageIncrease: newMonthlyPayment.minus(currentPaymentWithoutArrears).div(currentPaymentWithoutArrears).times(100),
    remainingMonths: parseInt(remainingMonths, 10),
    schedule: newSchedule
  };
}

/**
 * Restructure loan by extending the term
 * @param {number|string} currentBalance - Current outstanding balance
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} newTermMonths - The new desired term in months
 * @param {number|string} remainingMonths - Original remaining months in loan term
 * @param {number|string} arrearsAmount - Amount in arrears to be included
 * @param {number|string} originalPrincipal - Original loan amount (optional, defaults to currentBalance)
 * @param {number|string} originalTermMonths - Original loan term in months (optional, defaults to remainingMonths)
 * @returns {Object} Restructured payment information with extended term
 */
export function extendLoanTerm(currentBalance, annualInterestRate, newTermMonths, remainingMonths, arrearsAmount = 0, originalPrincipal = null, originalTermMonths = null) {
  const balance = new Decimal(currentBalance);
  const arrears = new Decimal(arrearsAmount);
  const rate = new Decimal(annualInterestRate);
  const newTerm = new Decimal(newTermMonths);
  const originalTerm = new Decimal(remainingMonths);
  
  // Use original terms if provided, otherwise fall back to current terms
  const origPrincipal = originalPrincipal ? new Decimal(originalPrincipal) : balance;
  const origTerm = originalTermMonths ? parseInt(originalTermMonths, 10) : parseInt(remainingMonths, 10);
  
  // Validate that new term is longer than remaining term
  if (newTerm.lessThanOrEqualTo(originalTerm)) {
    return {
      error: true,
      message: 'New term must be longer than the remaining term'
    };
  }
  
  // New balance includes arrears
  const newBalance = balance.plus(arrears);
  
  // Calculate the new monthly payment for the extended term
  const newMonthlyPayment = calculateMonthlyPayment(newBalance, annualInterestRate, newTerm);
  
  // Calculate the original monthly payment for comparison - should be based on original loan terms
  const originalMonthlyPayment = calculateMonthlyPayment(origPrincipal, annualInterestRate, origTerm);
  
  // Generate new schedule
  const newSchedule = generateAmortizationSchedule(newBalance, annualInterestRate, newTerm);
  
  // Calculate total interest
  const totalInterest = newMonthlyPayment.times(newTerm).minus(newBalance);
  
  return {
    originalBalance: balance,
    arrearsAmount: arrears,
    newBalance,
    originalPrincipal: origPrincipal,
    originalTermMonths: origTerm,
    originalMonthlyPayment,
    newMonthlyPayment,
    originalTermRemaining: originalTerm,
    newTermMonths: newTerm,
    additionalMonths: newTerm.minus(originalTerm),
    totalInterest,
    schedule: newSchedule
  };
}

/**
 * Restructure loan by reducing monthly payment
 * @param {number|string} currentBalance - Current outstanding balance
 * @param {number|string} annualInterestRate - Annual interest rate (in percentage)
 * @param {number|string} desiredMonthlyPayment - Desired lower monthly payment
 * @param {number|string} arrearsAmount - Amount in arrears to be included
 * @returns {Object} Restructured payment information with extended term and reduced payment
 */
export function reducedPaymentPlan(currentBalance, annualInterestRate, desiredMonthlyPayment, arrearsAmount = 0) {
  const balance = new Decimal(currentBalance);
  const payment = new Decimal(desiredMonthlyPayment);
  const arrears = new Decimal(arrearsAmount);
  const rate = new Decimal(annualInterestRate);
  
  // New balance includes arrears
  const newBalance = balance.plus(arrears);
  
  // Calculate monthly interest to check if payment is sufficient
  const monthlyRate = rate.div(100).div(12);
  const monthlyInterest = newBalance.times(monthlyRate);
  
  // Check if desired payment is at least higher than the monthly interest
  if (payment.lessThanOrEqualTo(monthlyInterest)) {
    return {
      error: true,
      message: 'Desired payment is too low to cover interest. Increase the payment amount.'
    };
  }
  
  // Calculate new term based on reduced payment
  const factor1 = new Decimal(1).minus(newBalance.times(monthlyRate).div(payment));
  const numerator = factor1.log().neg();
  const denominator = new Decimal(1).plus(monthlyRate).log();
  let newTerm = numerator.div(denominator).ceil();
  
  // Generate new schedule
  const newSchedule = generateAmortizationSchedule(newBalance, annualInterestRate, newTerm);
  
  // Calculate total interest
  const totalInterest = payment.times(newTerm).minus(newBalance);
  
  return {
    originalBalance: balance,
    arrearsAmount: arrears,
    newBalance,
    newMonthlyPayment: payment,
    newTermMonths: newTerm,
    totalInterest,
    schedule: newSchedule
  };
}

/**
 * Apply interest freeze for a specific period
 * @param {Array} schedule - Original amortization schedule
 * @param {number} freezeStartMonth - Month to start interest freeze
 * @param {number} freezeEndMonth - Month to end interest freeze
 * @returns {Array} Updated schedule with interest freeze
 */
export function applyInterestFreeze(schedule, freezeStartMonth, freezeEndMonth) {
  // Create a deep copy of the schedule to avoid modifying the original
  const updatedSchedule = schedule.map(payment => ({
    month: payment.month,
    payment: new Decimal(payment.payment),
    principal: new Decimal(payment.principal),
    interest: new Decimal(payment.interest),
    totalInterest: new Decimal(payment.totalInterest),
    balance: new Decimal(payment.balance)
  }));
  
  // Ensure freeze months are valid
  const startMonth = Math.max(1, freezeStartMonth);
  const endMonth = Math.min(schedule.length, freezeEndMonth);
  
  console.log('Applying interest freeze from month', startMonth, 'to', endMonth);
  
  // Apply interest freeze for the specified months
  for (let i = 0; i < updatedSchedule.length; i++) {
    const payment = updatedSchedule[i];
    
    // If this payment is within the freeze period
    if (payment.month >= startMonth && payment.month <= endMonth) {
      console.log('Freezing interest for month', payment.month, 'original interest:', payment.interest.toFixed(2));
      
      // Store the original interest for reference
      payment.originalInterest = new Decimal(payment.interest);
      
      // During freeze, all payment goes to principal, interest becomes zero
      payment.principal = new Decimal(payment.payment);
      payment.interest = new Decimal(0);
      
      // Mark this payment as having frozen interest
      payment.interestFrozen = true;
      
      console.log('Month', payment.month, 'after freeze - principal:', payment.principal.toFixed(2), 'interest:', payment.interest.toFixed(2));
    }
  }
  
  // Recalculate balances from the freeze start month onwards
  // Find the balance from the month before the freeze starts
  let currentBalance;
  if (startMonth === 1) {
    // If freeze starts from month 1, get the initial principal from the first payment
    currentBalance = updatedSchedule[0].balance.plus(updatedSchedule[0].principal);
  } else {
    // Get balance from the month before freeze starts
    currentBalance = new Decimal(updatedSchedule[startMonth - 2].balance);
  }
  
  console.log('Starting balance for recalculation:', currentBalance.toFixed(2));
  
  // Recalculate from the freeze start month onwards
  for (let i = startMonth - 1; i < updatedSchedule.length; i++) {
    const payment = updatedSchedule[i];
    
    // Update the balance by subtracting the principal payment
    currentBalance = currentBalance.minus(payment.principal);
    payment.balance = currentBalance.isNegative() ? new Decimal(0) : currentBalance;
    
    console.log('Month', payment.month, 'recalculated balance:', payment.balance.toFixed(2));
    
    // If we've paid off the loan early, mark remaining payments as zero
    if (currentBalance.isZero() || currentBalance.isNegative()) {
      // Zero out all subsequent payments
      for (let j = i + 1; j < updatedSchedule.length; j++) {
        updatedSchedule[j].payment = new Decimal(0);
        updatedSchedule[j].principal = new Decimal(0);
        updatedSchedule[j].interest = new Decimal(0);
        updatedSchedule[j].balance = new Decimal(0);
        console.log('Zeroing out month', updatedSchedule[j].month);
      }
      break;
    }
  }
  
  // Recalculate cumulative interest
  let cumulativeInterest = new Decimal(0);
  for (let i = 0; i < updatedSchedule.length; i++) {
    cumulativeInterest = cumulativeInterest.plus(updatedSchedule[i].interest);
    updatedSchedule[i].totalInterest = cumulativeInterest;
  }
  
  console.log('Final schedule after interest freeze applied');
  
  return addRemainingToRepayToSchedule(updatedSchedule);
}
